/**
 * Shared entry point for cart sync, used by both bundles.
 *
 * wp-custom.js loads only on product and cart pages; assets/cart-sync.js covers
 * the rest of the storefront so a buyer who lands on the homepage gets their
 * cart before they ever open the cart page. Both call this, and the
 * `__wpCartSyncBooted` guard makes a double-load harmless — the two bundles are
 * separate IIFEs with independent module state, so initializing twice would mean
 * two lock holders, two debounce timers and two pollers.
 */

import {
  beaconClear,
  fetchHistory,
  initCartSync,
  isSyncEnabled,
  requestClear,
  restoreVersion,
} from "./syncService.js";

/**
 * Start sync if — and only if — Liquid says this is a signed-in B2B customer
 * and the theme setting is on. Everything is gated server-side in Liquid, so a
 * logged-out or non-B2B visitor never even downloads a customer key.
 */
export function bootCartSync(options: { collaborative: boolean }): void {
  if (window.__wpCartSyncBooted) return;
  if (!window.__wpCartSyncEnabled) return;

  // Never sync from a theme-editor preview. Its cart is a scratch cart, and
  // pushing it would write a staff member's preview into a real customer's
  // synced cart.
  const shopify = window.Shopify as (typeof window.Shopify & { designMode?: boolean }) | undefined;
  if (shopify?.designMode) return;

  const customerKey = window.__wpCartSyncCustomerKey;
  if (!customerKey) return;

  // Fail closed on an unresolved location. Liquid emits null when none of the
  // three documented lookups produced one; syncing anyway would mean writing to
  // key 0, which merges a multi-location buyer's separate carts together
  // irreversibly. Not syncing just leaves the cart device-local, as it is today.
  const rawLocation = window.__wpCartSyncLocation;
  if (rawLocation === null || rawLocation === undefined) {
    console.debug(
      "[cart-sync] no company location resolved; staying device-local",
      { availableLocations: window.__wpCartSyncLocationCount ?? 0 },
    );
    return;
  }

  const locationId = Number(rawLocation);
  if (!Number.isSafeInteger(locationId) || locationId < 0) return;

  window.__wpCartSyncBooted = true;

  // Bridge for the cart page's inline script, which lives in Liquid and cannot
  // import from the bundle.
  window.__wpCartSync = {
    requestClear,
    beaconClear,
    fetchHistory,
    restoreVersion,
    isEnabled: isSyncEnabled,
  };

  initCartSync({
    customerKey,
    locationId,
    collaborative: options.collaborative,
  });

  // The cart page's inline script is parsed and its DOMContentLoaded listener
  // registered before this bundle's jQuery-ready callback runs, so it can look
  // for window.__wpCartSync and find nothing. Announce readiness so it can wire
  // up late as well as early.
  document.dispatchEvent(new CustomEvent("cart-sync:ready"));
}
